// ADOPS-2722 — When duplicating a line item or ad group, some settings from the
// original get carried over silently and have caused unexpected spend. These helpers
// detect the risky settings in the source record so the duplicate modal can warn the
// user and, where possible, offer a control to exclude the setting from the clone.

export interface CloneWarningField {
  // Full react-hook-form field name for the deselect checkbox (checked = copy to clone).
  fieldName: string;
  // Checkbox / bullet label shown to the user.
  label: string;
  // Short explanation of the risk shown under the label.
  detail: string;
  // When false the item is informational only (no checkbox) because the control lives
  // elsewhere in the form (e.g. creative flighting is toggled by "Copy Custom Delivery
  // Profiles").
  deselectable: boolean;
}

// Risky settings on a line item. `lineItem` is the camelCased LineItem passed to the
// duplicate modal (see mapLineItemToForm).
export const getLineItemCloneWarnings = (
  lineItem: any,
  fieldNamePrefix = 'new_insertion_order'
): CloneWarningField[] => {
  const warnings: CloneWarningField[] = [];

  if (lineItem?.frequencyCapEnabled || lineItem?.performanceCapEnabled) {
    warnings.push({
      fieldName: `${fieldNamePrefix}.copyFrequencyCaps`,
      label: 'Copy frequency caps',
      detail: 'Frequency and/or click frequency caps from the original will be carried over.',
      deselectable: true,
    });
  }

  if ((lineItem?.customDeliveryProfiles?.length ?? 0) > 0) {
    warnings.push({
      fieldName: `${fieldNamePrefix}.copyDeliveryProfiles`,
      label: 'Creative flighting (custom delivery profiles)',
      detail: 'Use "Copy Custom Delivery Profiles" below to include or exclude these.',
      deselectable: false,
    });
  }

  // Match a literal 0 only. Number(null)/Number('') are also 0, which would wrongly flag an
  // unset limit, so compare against 0 / '0' explicitly.
  if (lineItem?.dailyImpressionLimit === 0 || lineItem?.dailyImpressionLimit === '0') {
    warnings.push({
      fieldName: `${fieldNamePrefix}.copyDailyImpressionLimit`,
      label: 'Copy Daily Impression Limit (currently 0)',
      detail: 'A Daily Impression Limit of 0 will be carried over. Uncheck to leave it unset on the clone.',
      deselectable: true,
    });
  }

  if (lineItem?.dynamicSpendPacing === true) {
    warnings.push({
      fieldName: `${fieldNamePrefix}.copyDynamicSpendPacing`,
      label: 'Copy Dynamic Spend Pacing (currently on)',
      detail: 'Dynamic Spend Pacing will be enabled on the clone, which can spend automatically.',
      deselectable: true,
    });
  }

  return warnings;
};

// Risky settings on an ad group. `adGroup` is the AdGroup passed to the duplicate modal;
// its targeting profile inner keys are snake_case (see AdGroupSchema).
export const getAdGroupCloneWarnings = (adGroup: any): CloneWarningField[] => {
  const warnings: CloneWarningField[] = [];

  const hours = adGroup?.targetingProfile?.hours ?? adGroup?.targeting_profile?.hours;
  if (Array.isArray(hours) && hours.length > 0) {
    warnings.push({
      fieldName: 'copy_time_parting',
      label: 'Copy time parting (time targeting)',
      detail: 'Day/hour time targeting from the original will be carried over.',
      deselectable: true,
    });
  }

  return warnings;
};
